import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { CURRENCY_CODE } from '../../config/brand';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Layers, Plus, Pencil, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const defaultService = { name: '', description: '', duration: 60, price: 0, species_eligibility: ['dog', 'cat'], coat_type_suitability: [], requires_vaccination: false, is_addon: false, active: true };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultService);

  const fetchServices = async () => {
    try { const res = await api.get('/services/all'); setServices(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchServices(); }, []);

  const handleOpen = (svc = null) => {
    if (svc) { setEditing(svc.service_id); setForm(svc); }
    else { setEditing(null); setForm(defaultService); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Service name required'); return; }
    try {
      const payload = { ...form, price: parseFloat(form.price), duration: parseInt(form.duration) };
      if (editing) { await api.put(`/services/${editing}`, payload); toast.success('Service updated'); }
      else { await api.post('/services', payload); toast.success('Service created'); }
      setDialogOpen(false);
      fetchServices();
    } catch { toast.error('Failed to save service'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/services/${id}`); toast.success('Service deleted'); fetchServices(); }
    catch { toast.error('Failed to delete'); }
  };

  const mainServices = services.filter(s => !s.is_addon);
  const addonServices = services.filter(s => s.is_addon);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Service Catalog</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{services.length} services</p>
        </div>
        <Button className="rounded-full bg-[#2D2D2D] text-white hover:bg-[#1A1A1A] shadow-md" onClick={() => handleOpen()} data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      {/* Main Services */}
      <div>
        <h2 className="text-lg font-semibold text-[#2D2D2D] mb-3">Main Services</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {mainServices.map(svc => (
            <Card key={svc.service_id} className={`rounded-2xl border-[#E5E5E0] bg-white card-hover ${!svc.active ? 'opacity-60' : ''}`} data-testid={`service-card-${svc.service_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-[#2D2D2D]">{svc.name}</h3>
                  <div className="flex gap-1">
                    <button onClick={() => handleOpen(svc)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]" data-testid={`edit-service-${svc.service_id}`}><Pencil className="w-4 h-4 text-[#888]" /></button>
                    <button onClick={() => handleDelete(svc.service_id, svc.name)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-service-${svc.service_id}`}><Trash2 className="w-4 h-4 text-[#D9534F]" /></button>
                  </div>
                </div>
                <p className="text-sm text-[#5A5A5A] mb-3 line-clamp-2">{svc.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-[#E07A5F]">&#8377;{svc.price}</span>
                  <span className="text-xs text-[#888] flex items-center gap-1"><Clock className="w-3 h-3" />{svc.duration} min</span>
                </div>
                {svc.requires_vaccination && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full mt-2 inline-block">Vaccination required</span>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <h2 className="text-lg font-semibold text-[#2D2D2D] mb-3">Add-on Services</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {addonServices.map(svc => (
            <Card key={svc.service_id} className={`rounded-2xl border-[#E5E5E0] bg-white card-hover ${!svc.active ? 'opacity-60' : ''}`} data-testid={`addon-card-${svc.service_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-[#2D2D2D] text-sm">{svc.name}</h3>
                  <p className="text-xs text-[#888]">{svc.duration} min | &#8377;{svc.price}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleOpen(svc)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]"><Pencil className="w-3 h-3 text-[#888]" /></button>
                  <button onClick={() => handleDelete(svc.service_id, svc.name)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3 h-3 text-[#D9534F]" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit Service' : 'Add Service'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-lg" data-testid="service-name-input" /></div>
            <div><Label className="text-sm">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-lg" rows={2} data-testid="service-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Duration (min)</Label><Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-1 rounded-lg" data-testid="service-duration-input" /></div>
              <div><Label className="text-sm">Price ({CURRENCY_CODE})</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 rounded-lg" data-testid="service-price-input" /></div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.is_addon} onCheckedChange={(c) => setForm({ ...form, is_addon: c })} data-testid="service-addon-switch" /><Label className="text-sm">Add-on</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.requires_vaccination} onCheckedChange={(c) => setForm({ ...form, requires_vaccination: c })} data-testid="service-vacc-switch" /><Label className="text-sm">Requires Vaccination</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(c) => setForm({ ...form, active: c })} data-testid="service-active-switch" /><Label className="text-sm">Active</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={handleSave} data-testid="save-service-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
